import React, { useState } from 'react'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import PropTypes from 'prop-types'
import FAQ from '@components/faq/faq'
import TestimonialV2 from '@components/testimonialv2'
import FloatingBox from '@components/floating-box'
import CardsSection from '@components/cards-section'
import TextField from '@material-ui/core/TextField'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import MenuItem from '@material-ui/core/MenuItem'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, ThemeProvider } from '@material-ui/core/styles'
import VideoSpotlight from '@components/video-spotlight'
import { themesMapper } from '@config/mui-theme'
import CardSelector from '@components/card-selector/card-selector'
import { useStyles } from './styles'

const makeIdFromString = (str) => {
  if (!str) return null
  return str.toLowerCase().replace(/\s+/g, '-')
}

function ContentSections({ sections, includeSubNav, slug }) {
  const styles = useStyles()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const sectionNames = sections
    .map(({ anchorTitle }) => anchorTitle)
    .filter(Boolean)

  const [selectedSection, setSelectedSection] = useState(sectionNames[0])

  const handleSectionNameClick = (sectionName) => {
    setSelectedSection(sectionName)
    const offset = isMobile ? 45 : 90
    const sectionId = makeIdFromString(sectionName)
    const section = document.getElementById(sectionId)
    const sectionPosition = section.offsetTop
    window.scrollTo({
      top: sectionPosition - offset,
      behavior: 'smooth',
    })
  }

  const renderNav = (showNav, names) => {
    if (!showNav) return null
    return (
      <Container className={styles.menuRoot}>
        {!isMobile ? (
          names.map((sectionName) => (
            <Button
              variant="text"
              color="secondary"
              onClick={() => handleSectionNameClick(sectionName)}
              key={sectionName}
            >
              {sectionName}
            </Button>
          ))
        ) : (
          <div className={styles.mobileMenu}>
            <TextField
              select
              value={selectedSection}
              onChange={(e) => handleSectionNameClick(e.target.value)}
              className={styles.input}
              SelectProps={{
                displayEmpty: true,
                IconComponent: () => (
                  <KeyboardArrowDownIcon
                    className={styles.selectArrow}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      e.currentTarget.parentNode.firstChild.click()
                    }}
                  />
                ),
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                  classes: { paper: styles.selectPaper },
                },
              }}
            >
              {names.map((el) => (
                <MenuItem
                  key={el}
                  value={el}
                  className={styles.menuItem}
                  aria-label={`Display content, ${el}`}
                >
                  {el}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}
      </Container>
    )
  }

  const renderContentSections = (contentSections) => {
    return (
      <div>
        {contentSections.map((section) => {
          switch (section.__typename) {
          case 'BasicCta':
            return (
              <ThemeProvider theme={themesMapper(section.theme)}>
                <div id={makeIdFromString(section.anchorTitle)}>
                  {section.card ? (
                    <FloatingBox
                      {...section}
                      flipped={section.mediaPlacement === 'Right'}
                    />
                  ) : (
                    <VideoSpotlight
                      flipped={section.mediaPlacement === 'Left'}
                      videoSpotlight={section}
                    />
                  )}
                </div>
              </ThemeProvider>
            )
          case 'Accordion':
            return (
              <div id={makeIdFromString(section.anchorTitle)}>
                <FAQ
                  {...section}
                  collectionName="customPageCollection"
                  slug={slug}
                />
              </div>
            )
          case 'TestimonialV2':
            return (
              <div id={makeIdFromString(section.anchorTitle)}>
                <TestimonialV2 testimonialsData={section} />
              </div>
            )
          case 'SectionCards':
            return (
              <ThemeProvider theme={themesMapper(section.theme)}>
                <div id={makeIdFromString(section.anchorTitle)}>
                  <CardsSection {...section} />
                </div>
              </ThemeProvider>
            )
          case 'SectionTabs':
            return (
              <div id={makeIdFromString(section.anchorTitle)}>
                <CardSelector {...section} />
              </div>
            )
          default:
            return null
          }
        })}
      </div>
    )
  }

  return (
    <>
      {renderNav(includeSubNav, sectionNames)}
      {renderContentSections(sections)}
    </>
  )
}

ContentSections.propTypes = {
  sections: PropTypes.array.isRequired,
  includeSubNav: PropTypes.bool,
  slug: PropTypes.string,
}

export default ContentSections
